/**
 * Staff role permissions, mirroring the license-server's enforcement in
 * src/routes/internal.ts (the server stays authoritative; this only decides
 * what the UI offers):
 *
 *   - Every /internal/* route: owner/admin tokens must carry mfa_ok,
 *     otherwise 403 mfa_required (including reads).
 *   - Reads: all four roles, EXCEPT the routes that call
 *     requireMutationAuth even on GET (staff list, staff/me, timing
 *     settings, customer notes, release-candidate, next-suggested) which
 *     are owner/admin only.
 *   - Writes (create / edit / issue / revoke / suspend / publish / reply /
 *     triage ...): requireMutationAuth => owner or admin.
 *   - Destructive deletes and staff management: requireOwner => owner.
 *
 * Support and Readonly therefore have identical, read-only access.
 *
 *   - Activation keys: the server returns the full key only to owner/admin
 *     (MFA finished); support/readonly get `****abcd` plus
 *     `activation_key_redacted: true`. `canViewKeys` mirrors that; the
 *     response flag is still what pages render from.
 *   - Tickets: reply / assign / status / attachments are requireMutationAuth
 *     writes (owner/admin). There is no Support reply capability: ADR-008
 *     defines support as view-only and widening it needs owner approval.
 */
import { useMemo } from 'react';
import { useSession, type SessionUser } from '../session';

export type StaffRoleName = SessionUser['role'];

export interface Permissions {
  role: StaffRoleName | null;
  /** Role may call requireMutationAuth endpoints (owner/admin) AND has finished MFA. */
  canWrite: boolean;
  /** requireOwner endpoints (delete, staff management) AND MFA finished. */
  canOwner: boolean;
  /** Role receives full activation keys from the server (owner/admin with MFA finished). */
  canViewKeys: boolean;
  /** Role may read the owner/admin-only GET routes (staff list, settings, notes). */
  canReadAdminData: boolean;
  /** Plain-language reason writes are unavailable, or null when allowed. */
  writeReason: string | null;
  /** Plain-language reason owner-only actions are unavailable, or null when allowed. */
  ownerReason: string | null;
}

const LABEL: Record<StaffRoleName, string> = {
  owner: 'Owner', admin: 'Admin', support: 'Support', readonly: 'Read-only',
};

export function permissionsFor(user: SessionUser | null | undefined): Permissions {
  if (!user) {
    return {
      role: null, canWrite: false, canOwner: false, canViewKeys: false, canReadAdminData: false,
      writeReason: 'Sign in to make changes.', ownerReason: 'Sign in to make changes.',
    };
  }
  const privileged = user.role === 'owner' || user.role === 'admin';
  const mfaPending = privileged && !user.mfa_ok;
  const canWrite = privileged && !mfaPending;
  const canOwner = user.role === 'owner' && !mfaPending;
  const mfaReason = 'Finish two-factor sign-in to make changes.';
  return {
    role: user.role,
    canWrite,
    canOwner,
    canViewKeys: canWrite,
    canReadAdminData: canWrite,
    writeReason: canWrite
      ? null
      : mfaPending
        ? mfaReason
        : `Your ${LABEL[user.role]} role can view this but not make changes. Owner and Admin accounts can.`,
    ownerReason: canOwner
      ? null
      : mfaPending
        ? mfaReason
        : user.role === 'admin'
          ? 'Only an Owner account can do this.'
          : `Only an Owner account can do this. Your ${LABEL[user.role]} role can view but not change this.`,
  };
}

export function usePermissions(): Permissions {
  const { state } = useSession();
  const user = state.user;
  return useMemo(() => permissionsFor(user), [user]);
}
